import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => useContext(AdminAuthContext);

const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await adminApi.get("/auth/me");
        if (active) setUser(data);
      } catch {
        try {
          const { data } = await adminApi.post("/auth/refresh");
          if (active) setUser(data);
        } catch {
          if (active) setUser(false);
        }
      }
    };
    check();
    return () => {
      active = false;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await adminApi.post("/auth/login", { email, password });
    setUser(data);
  };

  const logout = async () => {
    try {
      await adminApi.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  return <AdminAuthContext.Provider value={{ user, setUser, login, logout }}>{children}</AdminAuthContext.Provider>;
};

export const AdminLayout = () => {
  useEffect(() => {
    const robots = document.head.querySelector('meta[name="robots"]');
    const previous = robots?.getAttribute("content");
    robots?.setAttribute("content", "noindex, nofollow");
    return () => {
      if (robots && previous) robots.setAttribute("content", previous);
    };
  }, []);

  return (
    <AdminAuthProvider>
      <div className="relative min-h-screen bg-[#0A0B0D] text-white">
        <Outlet />
      </div>
    </AdminAuthProvider>
  );
};

export const RequireAdmin = ({ children }) => {
  const { user } = useAdminAuth();
  if (user === null) {
    return (
      <div className="grid min-h-screen place-items-center" data-testid="admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#A1A1AA]" aria-label="Loading" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};
